import React, { useCallback, useEffect, useMemo, useState } from 'react'
import style from '../pendingApproval/pending.module.css'
import BoxComponent from '../ReuseComponent/BoxComponent'
import CardComponent from '../ReuseComponent/CardComponent'
import { database } from '../../utils/firebase'
import { path } from '../../utils/const'
import BackComponent from '../../components/BackComponent/BackComponent'
import { useDispatch, useSelector } from 'react-redux'
import { Avatar, Button, DatePicker, Divider, Drawer, Flex, Input, Radio, Space, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { actions as appActions } from 'slices/app.slice'

const DATE_RANGE_TTL_MS = 1000 * 60 * 60 * 6 // 6 hours


const PastConsultationPage = () => {
  const dispatch = useDispatch()
  const [pastConsultation, setPastConsultation] = useState([])
  const [pastCount, setPastCount] = useState(0)
  const [lastTimestamp, setLastTimestamp] = useState(null)
  const [searchValue, setSearchValue] = useState('')
  const [afterSearchValue, setAfterSearchValue] = useState(false)
  const [showHide, setShowHide] = useState(false)
  const defaultIncrement = 50
  const [dataLimit, setDataLimit] = useState(defaultIncrement)
  const [search, setSearch] = useState('')
  const [selectedDateRange, setSelectedDateRange] = useState(null)
  const [dateFilteredConsultations, setDateFilteredConsultations] = useState(null)
  const { RangePicker } = DatePicker
  const { me, pastConsultationDateRange } = useSelector((state) => state.app)
  const [hasHydratedRange, setHasHydratedRange] = useState(false)
  const clearPersistedRangeSelection = useCallback(() => {
    setSelectedDateRange(null)
    dispatch(appActions.clearPastConsultationDateRange())
  }, [dispatch])
  const searchfunction = async (name) => {
    try {
      if (selectedDateRange) {
        const lookup = (name || '').trim().toLowerCase()
        const baseList = dateFilteredConsultations || []
        if (!lookup) {
          setPastConsultation(baseList)
          setPastCount(baseList.length)
          setSearch('')
          setAfterSearchValue(false)
          return
        }
        const filtered = baseList.filter((convo) =>
          (convo.patientName || '').toLowerCase().includes(lookup)
        )
        setPastConsultation(filtered)
        setPastCount(filtered.length)
        setSearch(name)
        setAfterSearchValue(false)
        return
      }
      const lookupRaw = (name || '').trim()
      if (!lookupRaw) {
        clearPersistedRangeSelection()
        setPastConsultation([])
        setPastCount(0)
        setSearch('')
        setAfterSearchValue(false)
        return
      }
      clearPersistedRangeSelection()
      const variations = Array.from(new Set([
        lookupRaw,
        lookupRaw.toLowerCase(),
        lookupRaw.toUpperCase(),
        lookupRaw
          .split(' ')
          .map((part) => part ? part[0].toUpperCase() + part.slice(1).toLowerCase() : part)
          .join(' ')
      ]))
      const snapshots = await Promise.all(
        variations.map((variant) =>
          database
            .ref(`/delegateChats`)
            .orderByChild('patientName')
            .startAt(variant)
            .endAt(`${variant}\uf8ff`)
            .limitToLast(defaultIncrement)
            .once('value')
        )
      )
      const combined = {}
      snapshots.forEach((snapshot) => {
        Object.assign(combined, snapshot.val() || {})
      })

      const archiveKeys = Object.keys(combined)

      let temp = archiveKeys
        .map((key) => ({
          ...combined[key],
          key
        }))
        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
        .filter((obj) => obj.status === 'closed')

      if (me.role === 'Doctor') {
        temp = temp.filter(
          (convo) => convo.doctorID === me.id,
        )
      }

      setPastConsultation(temp)
      setAfterSearchValue(true)
      setSearch(lookupRaw)
      const count = temp.length
      setPastCount(count)
    }
    catch (e) {
      console.log(e)
    }
  };

  const normalizeConsultations = useCallback((conversations = {}) => {
    const archiveKeys = Object.keys(conversations)
    let temp = archiveKeys
      .map((key) => ({ ...conversations[key], key }))
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
      .filter((obj) => obj.status === 'closed')
    if (me.role === 'Doctor') {
      temp = temp.filter((convo) => convo.doctorID === me.id)
    }
    return temp
  }, [me])

  const loadRecentConsultations = useCallback(async () => {
    try {
      const archiveSnapshot = await database
        .ref(`/delegateChats`)
        .orderByChild('timestamp')
        .limitToLast(defaultIncrement)
        .once('value')
      const archiveConversationsData = archiveSnapshot.toJSON() || {}
      const arr = Object.values(archiveConversationsData)
      const lastTime = arr[0]?.timestamp
      setLastTimestamp(lastTime ?? null)
      const temp = normalizeConsultations(archiveConversationsData)
      setPastConsultation(temp)
      setPastCount(temp.length)
      setDateFilteredConsultations(null)
    } catch (e) {
      console.log(e)
    }
  }, [defaultIncrement, normalizeConsultations])

  const loadConsultationsByDate = useCallback(async (startTime, endTime) => {
    try {
      const archiveSnapshot = await database
        .ref(`/delegateChats`)
        .orderByChild('timestamp')
        .startAt(startTime)
        .endAt(endTime)
        .once('value')
      const conversations = archiveSnapshot.toJSON() || {}
      const temp = normalizeConsultations(conversations)
      setPastConsultation(temp)
      setPastCount(temp.length)
      setDateFilteredConsultations(temp)
      setShowHide(true)
      setAfterSearchValue(false)
      setDataLimit(defaultIncrement)
      setSearch('')
      setLastTimestamp(null)
    } catch (e) {
      console.log(e)
    }
  }, [defaultIncrement, normalizeConsultations])

  useEffect(() => {
    if (hasHydratedRange) {
      return
    }
    const hydrate = async () => {
      const storedRange = pastConsultationDateRange?.range
      const storedAt = pastConsultationDateRange?.storedAt
      const hasRange = Array.isArray(storedRange) && storedRange.length === 2
      const expired = !storedAt || (Date.now() - storedAt) > DATE_RANGE_TTL_MS
      if (hasRange && !expired) {
        setSelectedDateRange(storedRange)
        await loadConsultationsByDate(storedRange[0], storedRange[1])
      } else {
        if (expired && pastConsultationDateRange) {
          dispatch(appActions.clearPastConsultationDateRange())
        }
        await loadRecentConsultations()
      }
      setHasHydratedRange(true)
    }
    hydrate()
  }, [dispatch, hasHydratedRange, loadConsultationsByDate, loadRecentConsultations, pastConsultationDateRange])

  useEffect(() => {
    if (!selectedDateRange) {
      return
    }
    const storedAt = pastConsultationDateRange?.storedAt
    if (!storedAt) {
      return
    }
    const expiresIn = DATE_RANGE_TTL_MS - (Date.now() - storedAt)
    if (expiresIn <= 0) {
      clearPersistedRangeSelection()
      loadRecentConsultations()
      return
    }
    const timer = setTimeout(() => {
      clearPersistedRangeSelection()
      loadRecentConsultations()
    }, expiresIn)
    return () => clearTimeout(timer)
  }, [clearPersistedRangeSelection, loadRecentConsultations, pastConsultationDateRange, selectedDateRange])

  useEffect(() => {
    if (selectedDateRange && (!searchValue || !searchValue.trim())) {
      const baseList = dateFilteredConsultations || []
      setPastConsultation(baseList)
      setPastCount(baseList.length)
      setAfterSearchValue(false)
    }
  }, [searchValue, selectedDateRange, dateFilteredConsultations])

  const archiveConversations = async () => {
    if (selectedDateRange) {
      return
    }
    try {
     let archiveSnapshot;
     let resp;
     let temp;
     let arr;
     let delegateKeys;
     if (afterSearchValue) {

       archiveSnapshot = await database
         .ref(`/delegateChats`)
         .orderByChild('patientName')
         .equalTo(search)
         .limitToLast(dataLimit)
         .once('value')
       setDataLimit(dataLimit + defaultIncrement)
       resp = archiveSnapshot.toJSON() || {}

       delegateKeys = Object.keys(resp)

       if (delegateKeys.length !== dataLimit) {
         setShowHide(true)
       }
       temp = delegateKeys.map((key) => ({...resp[key], key})).reverse().filter((obj) => obj.status === 'closed')
       if (me.role === 'Doctor') {
         temp = temp.filter(
           (convo) => convo.doctorID ===  me.id,
         )
       }
       if (temp.length > 0) {
         setPastConsultation(temp)
         setPastCount(temp.length)
       }
     } else {
       let query = database
         .ref(`/delegateChats`)
         .orderByChild('timestamp');
       if (lastTimestamp){
         query = query.endBefore(lastTimestamp)
       }
       archiveSnapshot = await query
         .limitToLast(defaultIncrement)
         .once('value')
       resp = archiveSnapshot.toJSON() || {}
       delegateKeys = Object.keys(resp)
       arr = Object.values(resp)
       const laststamp = arr[0]?.timestamp
       setLastTimestamp(laststamp)
       if (delegateKeys.length !== defaultIncrement) {
         setShowHide(true)
       }
       temp = delegateKeys.map((key) => ({...resp[key], key})).reverse().filter((obj) => obj.status === 'closed')

       // temp.splice(temp.length-1, 1)

       const mergedArray = [...pastConsultation, ...temp];

       const uniqueArray = mergedArray.reduce((acc, obj) => {
         const key = obj.key;
         if (!acc[key]) {
           acc[key] = obj;
         }
         return acc;
       }, {});

       let result = Object.values(uniqueArray);
       if (me.role === 'Doctor') {
         result = result.filter(
           (convo) => convo.doctorID === me.id,
         )
       }
       setPastConsultation(result)

       setPastCount(result.length)
     }
   }
   catch (e) {
     console.log(e)
   }
  }
  const searchStringChecker = (string = '') => {
    if (!string) {
      return true
    }
    const lowercaseString = string.toLowerCase()
    const lowercaseSearchValue = searchValue.toLowerCase()
    return lowercaseString.includes(lowercaseSearchValue)
  }

  const onDateSelected = async (dates) => {
    if (dates && dates.length === 2) {
      const startTime = dates[0].startOf('day').valueOf()
      const endTime = dates[1].endOf('day').valueOf()
      const newRange = [startTime, endTime]
      setSelectedDateRange(newRange)
      dispatch(appActions.setPastConsultationDateRange({ range: newRange, storedAt: Date.now() }))
      await loadConsultationsByDate(startTime, endTime)
      return
    }
    clearPersistedRangeSelection()
    setShowHide(false)
    setAfterSearchValue(false)
    setDataLimit(defaultIncrement)
    await loadRecentConsultations()
  }

  const rangePickerValue = useMemo(() => {
    if (!selectedDateRange) {
      return null
    }
    return [dayjs(selectedDateRange[0]), dayjs(selectedDateRange[1])]
  }, [selectedDateRange])

  const handleDashboardNavigation = () => {
    clearPersistedRangeSelection()
  }

  return (
    <div className={style.pastScroll}>
      <BackComponent
        title={'Past consultations'}
        activeCount={pastCount}
        dashboard={path.dashboard}
        onNavigateToDashboard={handleDashboardNavigation}
      />
      <BoxComponent />
      <div className={style.line}></div>
      { <div className={style.searchDiv}>
        <input className={style.search} type="text" placeholder="Search patients" onChange={(event) => { setSearchValue(event.target.value) }} />
        <button className={style.searchButton} onClick={(event) =>searchfunction(searchValue)}>Search</button>
      </div> }

      <div className={style.line}></div>
      <div className={style.searchDate}>
        {/* <div className={style.dateText}>
          <strong>Date:</strong>
        </div> */}
        <RangePicker
          className={style.rangePicker}
          dropdownClassName={style.rangePickerDropdown}
          getPopupContainer={() => document.body}
          placement="bottomLeft"
          inputReadOnly
          onChange={onDateSelected}
          value={rangePickerValue}
        />
      </div>
      <div className={style.line}></div>

      {pastConsultation.map((obj,i) => (
          <div
            key={i}
            className={obj.action === 'declined' ? style.UnreadPatient : ''}
          >
            <CardComponent
              forwardPath={path.consultation}
              greenColor={'#38C763'}
              archiveID={obj.key}
              butttonChange="past"
              delegateData={obj}
            />
          </div>
        ))}
      <div className={style.bottomPadding} />
      {showHide ?<div> <button></button></div>:
        <div className={style.paginationButton}> <button className={style.button} onClick={archiveConversations}>Load More</button></div>
      }
    </div>
  )
}

export default PastConsultationPage
